import { ContactPerson, Contributor, Event, Exhibitor, Post, Product } from '@planone/shared/util-interfaces';
import {
  BreadcrumbData,
  BreadcrumbListItem,
  BreadcrumbStructuredDataListItem,
} from '@planone/shared/util-structured-data';

import { BusinessCard, Fair } from './breadcrumb.interfaces';

export class BreadcrumbBuilder {
  private elements: BreadcrumbListItem[] = [];

  private constructor(private bauInsights: string) {}

  private personName(person: ContactPerson): string {
    const first = person?.first_name ?? '';
    const second = person?.name ?? '';
    return (person?.title ? person?.title + ' ' : '') + first + ' ' + second;
  }

  private isBauInsights(fair?: Fair): boolean {
    return fair?.id === this.bauInsights;
  }

  static getInstance(bauInsightsId: string): BreadcrumbBuilder {
    return new BreadcrumbBuilder(bauInsightsId);
  }

  forRoot(disabled?: boolean): BreadcrumbBuilder {
    return this.withRoot(disabled);
  }

  forFair(fair: Fair, disableLastElement?: boolean): BreadcrumbBuilder {
    return this.forRoot().withAllFairs(fair).withFair(fair, disableLastElement);
  }

  forExhibitorDirectory(fair: Fair, disableLastElement?: boolean): BreadcrumbBuilder {
    return this.forFair(fair).withExhibitorDirectory(fair, disableLastElement);
  }

  forExhibitor(fair: Fair, exhibitor: Exhibitor, disableLastElement?: boolean): BreadcrumbBuilder {
    return this.forExhibitorDirectory(fair)
      .withExhibitorLetter(fair, exhibitor)
      .withExhibitorName(fair, exhibitor, disableLastElement);
  }

  with(element: BreadcrumbListItem): BreadcrumbBuilder {
    this.elements.push(element);
    return this;
  }

  withRoot(disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-newsfeed: Newsfeed`,
      routerLink: ['/'],
      disabled,
    });
  }

  withAllFairs(fair?: Fair, disabled?: boolean): BreadcrumbBuilder {
    if (this.isBauInsights(fair)) return this;
    return this.with({
      label: $localize`:@@utilBreadcrumb-allFairs: Alle Veranstaltungen`,
      routerLink: ['/', 'fairs'],
      disabled,
    });
  }

  withFair(fair: Fair, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: fair.name,
      routerLink: ['/', 'fairs', fair.id],
      disabled,
    });
  }

  withExhibitorDirectory(fair: Fair, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: this.isBauInsights(fair)
        ? $localize`:@@utilBreadcrumb-exhibitorDirectory-bauInsights: Unternehmensverzeichnis`
        : $localize`:@@utilBreadcrumb-exhibitorDirectory: Ausstellerverzeichnis`,
      routerLink: ['/', 'fairs', fair.id, 'exhibitors', 'letters'],
      queryParams: { character: 'A' },
      disabled,
    });
  }

  withExhibitorDirectoryLetter(fair: Fair, letter: string, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: letter,
      routerLink: ['/', 'fairs', fair.id, 'exhibitors', 'letters'],
      queryParams: { character: letter },
      disabled,
    });
  }

  withExhibitorDirectorySearch(fair: Fair, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-exhibitorDirectory-search: Suche`,
      routerLink: ['/', 'fairs', fair.id, 'exhibitors', 'search'],
      disabled,
    });
  }

  withExhibitorLetter(fair: Fair, exhibitor: Exhibitor, disabled?: boolean): BreadcrumbBuilder {
    const letter: string = (exhibitor.display_name ?? exhibitor.name)[0].toLocaleUpperCase();
    return this.withExhibitorDirectoryLetter(fair, letter, disabled);
  }

  withExhibitorName(fair: Fair, exhibitor: Exhibitor, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: exhibitor.display_name ?? exhibitor.name,
      routerLink: ['/', 'fairs', fair.id, 'exhibitors', exhibitor.id],
      disabled,
    });
  }

  withPostName(fair: Fair, post: Post, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: post.headline,
      routerLink: ['/', 'fairs', fair.id, 'posts', post.id],
      disabled,
    });
  }

  withProductName(fair: Fair, product: Product, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: product.name,
      routerLink: ['/', 'fairs', fair.id, 'products', product.id],
      disabled,
    });
  }

  withEventName(fair: Fair, event: Event, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: event.headline,
      routerLink: ['/', 'fairs', fair.id, 'events', event.id],
      disabled,
    });
  }

  withContactPersonName(
    fair: Fair,
    exhibitor: Exhibitor,
    contactPerson: ContactPerson,
    disabled?: boolean
  ): BreadcrumbBuilder {
    return this.with({
      label: this.personName(contactPerson),
      routerLink: ['/', 'fairs', fair.id, 'exhibitors', exhibitor.id, 'contact', contactPerson.id],
      disabled,
    });
  }

  withEventContributorName(fair: Fair, event: Event, contributor: Contributor, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: this.personName(contributor),
      routerLink: ['/', 'fairs', fair.id, 'events', event.id, 'contributors', contributor.id],
      disabled,
    });
  }

  withSearch(fair: Fair, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-search: Entdecken`,
      routerLink: ['/', 'fairs', fair.id, 'search'],
      disabled,
    });
  }

  withNewsfeed(disabled?: boolean): BreadcrumbBuilder {
    return this.withRoot(disabled);
  }

  withWatchlist(disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-watchlist: Merkliste`,
      routerLink: ['/', 'watchlist'],
      disabled,
    });
  }

  withUserProfile(disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-userProfile: Mein Konto`,
      routerLink: ['/', 'profile'],
      disabled,
    });
  }

  withMyBusinessCard(disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-userDigitalBusinessCard: Meine Visitenkarte`,
      routerLink: ['/', 'profile', 'business-card'],
      disabled,
    });
  }

  withBusinessCard(businessCard: BusinessCard, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-publicDigitalBusinessCard: Digitale Visitenkarte`,
      routerLink: ['/', 'business-card', businessCard.uuid ?? ''],
      disabled,
    });
  }

  withBusinessCardName(businessCard: BusinessCard, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: `${businessCard.first_name} ${businessCard.last_name}`,
      routerLink: ['/', 'business-card', businessCard.uuid ?? ''],
      disabled,
    });
  }

  withInvalidBusinessCard(disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-invalidBusinessCard: Ungültige Visitenkarte`,
      routerLink: ['/'],
      disabled,
    });
  }

  withUserProfileCompletion(disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-userProfileCompletion: Personalisierung`,
      routerLink: ['/', 'profilecompletion'],
      disabled,
    });
  }

  withProgram(fair: Fair, disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-program: Rahmenprogramm`,
      routerLink: ['/', 'fairs', fair.id, 'program'],
      disabled,
    });
  }

  withTickets(disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-tickets:Tickets`,
      routerLink: ['/', 'tickets'],
      disabled,
    });
  }

  withQRCodeSCanner(disabled?: boolean): BreadcrumbBuilder {
    return this.with({
      label: $localize`:@@utilBreadcrumb-qrCodeScanner: QR Code Scannen`,
      routerLink: ['/', 'scanner'],
      disabled,
    });
  }

  build(document: { location: Location }): BreadcrumbData {
    const { host, protocol } = document.location;
    const itemList: BreadcrumbStructuredDataListItem[] = [];

    this.elements.forEach((element, index) => {
      let url = host;

      element.routerLink.forEach((pathSegment, index) => {
        url = `${url}${index > 1 ? '/' : ''}${pathSegment}`;
      });

      itemList.push({
        '@type': 'ListItem',
        position: index + 1,
        item: protocol + '//' + url,
        name: element.label,
      });
    });

    return {
      elements: this.elements,
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: itemList,
      },
    };
  }
}
